/**
 * Preset Export Templates
 * Pre-configured templates for common platforms and patterns
 */

import { ExportTemplate } from '../../types/exportTemplates';
import { generateUUID } from '../utils/uuid';

/**
 * Roblox Table-Based Pattern
 * Simple table pattern matching the user's example
 */
export const ROBLOX_TABLE_PRESET: ExportTemplate = {
  id: 'preset-roblox-table',
  name: 'Roblox Table Pattern',
  platform: 'roblox',
  isPreset: true,
  description: 'Simple table-based pattern for Roblox. Creates type definitions and instance tables.',
  
  functions: [
    {
      name: 'GetData',
      description: 'Returns all data for this object',
      appliesTo: 'all',
      commonVariables: ['TemplateName', 'self'],
      content: `function {{TemplateName}}:GetData()
\treturn self
end`
    }
  ],
  
  typeFile: {
    enabled: true,
    filename: '{{TemplateName}}.luau',
    location: 'types',
    content: `-- {{TemplateName}}.luau
-- Auto-generated type definition

export type {{TemplateName}} = {
{{#each fields}}
\t{{fieldName}}: {{fieldType}}{{#if optional}}?{{/if}},
{{/each}}
}

local {{TemplateName}} = {}
return {{TemplateName}}
`
  },
  
  instanceFile: {
    enabled: true,
    filename: '{{FileName}}.luau',
    location: 'instances',
    content: `local {{TemplateName}} = require(game.ReplicatedStorage.Code.Types.{{TemplateName}})

local {{instanceName}}: {{TemplateName}}.{{TemplateName}} = {
{{#each fields}}
\t{{fieldName}} = {{fieldValue}},
{{/each}}
}

{{functions}}

return {{instanceName}}
`
  },
  
  fieldMappings: {
    title: { typeName: 'string', defaultValue: '""' },
    textarea: { typeName: 'string', defaultValue: '""' },
    text: { typeName: 'string', defaultValue: '""' },
    number: { typeName: 'number', defaultValue: '0' },
    file: { typeName: 'string', defaultValue: '""' },
    image: { typeName: 'string', defaultValue: '""' },
    url: { typeName: 'string', defaultValue: '""' },
    tags: { typeName: '{string}', defaultValue: '{}' },
    types: { typeName: 'string', defaultValue: '""' },
    glossaryItem: { typeName: 'string', defaultValue: '""' }
  },
  
  namingConventions: {
    templateName: 'PascalCase',
    fieldName: 'PascalCase',
    instanceName: 'snake_case',
    fileName: 'snake_case'
  },
  
  options: {
    includeComments: true,
    includeMetadata: false,
    wrapOptionalFields: true
  }
};

/**
 * Roblox OOP Pattern
 * Object-oriented pattern with base classes and inheritance
 */
export const ROBLOX_OOP_PRESET: ExportTemplate = {
  id: 'preset-roblox-oop',
  name: 'Roblox OOP Pattern',
  platform: 'roblox',
  isPreset: true,
  description: 'Object-oriented pattern with base modules and user extensions. Supports inheritance and method overrides.',
  
  typeFile: {
    enabled: true,
    filename: '{{TemplateName}}Base.luau',
    location: 'generated',
    content: `-- {{TemplateName}}Base.luau
-- <auto-generated>
-- This file is AUTO-GENERATED and can be safely regenerated.
-- DO NOT EDIT THIS FILE DIRECTLY - your changes will be lost.
-- Extend this module in your own files to add custom behavior.
-- </auto-generated>

local {{TemplateName}}Base = {}
{{TemplateName}}Base.__index = {{TemplateName}}Base

--[=[
\tData structure for {{TemplateName}}
]=]
export type {{TemplateName}}BaseData = {
\tobject_id: string,
\tname: string,
{{#each fields}}
\t{{fieldName}}{{#if optional}}?{{/if}}: {{fieldType}}, -- {{comment}}
{{/each}}
}

--[=[
\tBase class for {{TemplateName}} items
]=]
export type {{TemplateName}}Base = {
\t__index: {{TemplateName}}Base,
\t_data: {{TemplateName}}BaseData?,
\tnew: () -> {{TemplateName}}Base,
\tApplyData: (self: {{TemplateName}}Base, data: {{TemplateName}}BaseData) -> (),
\tGetData: (self: {{TemplateName}}Base) -> {{TemplateName}}BaseData?,
}

--[=[
\tCreates a new instance of {{TemplateName}}Base
\t@return {{TemplateName}}Base
]=]
function {{TemplateName}}Base.new(): {{TemplateName}}Base
\tlocal self = setmetatable({}, {{TemplateName}}Base)
\tself._data = nil
\treturn self
end

--[=[
\tApply data from JSON to this instance
\t@param data {{TemplateName}}BaseData -- The data to apply
]=]
function {{TemplateName}}Base:ApplyData(data: {{TemplateName}}BaseData)
\tif self.OnBeforeApply then
\t\tself:OnBeforeApply(data)
\tend
\t
\tself._data = data
\t
\tif self.OnAfterApply then
\t\tself:OnAfterApply(data)
\tend
end

--[=[
\tGet the data structure
\t@return {{TemplateName}}BaseData? -- The data or nil
]=]
function {{TemplateName}}Base:GetData(): {{TemplateName}}BaseData?
\treturn self._data
end

return {{TemplateName}}Base
`
  },
  
  instanceFile: {
    enabled: true,
    filename: '{{FileName}}.luau',
    location: 'user',
    content: `-- {{FileName}}.luau
-- USER FILE - Customize this file freely
-- This file will NEVER be overwritten when regenerating

local {{TemplateName}}Base = require(script.Parent.Parent.generated.{{TemplateName}}Base)

local {{ItemName}} = {}
{{ItemName}}.__index = {{ItemName}}
setmetatable({{ItemName}}, { __index = {{TemplateName}}Base })

--[=[
\tCustom implementation for {{ItemName}}
]=]
export type {{ItemName}} = {{TemplateName}}Base.{{TemplateName}}Base & {
\t-- Add your custom type definitions here
}

--[=[
\tCreates a new instance of {{ItemName}}
\t@return {{ItemName}}
]=]
function {{ItemName}}.new(): {{ItemName}}
\tlocal self = {{TemplateName}}Base.new()
\tsetmetatable(self, { __index = {{ItemName}} })
\treturn self
end

--[=[
\tOverride to add custom initialization after data is loaded
\t@param data {{TemplateName}}Base.{{TemplateName}}BaseData
]=]
function {{ItemName}}:OnAfterApply(data: {{TemplateName}}Base.{{TemplateName}}BaseData)
\t-- Add your custom initialization here
end

{{functions}}

return {{ItemName}}
`
  },
  
  fieldMappings: {
    title: { typeName: 'string', defaultValue: '""' },
    textarea: { typeName: 'string', defaultValue: '""' },
    text: { typeName: 'string', defaultValue: '""' },
    number: { typeName: 'number', defaultValue: '0' },
    file: { typeName: 'string', defaultValue: '""' },
    image: { typeName: 'string', defaultValue: '""' },
    url: { typeName: 'string', defaultValue: '""' },
    tags: { typeName: '{string}', defaultValue: '{}' },
    types: { typeName: 'string', defaultValue: '""' },
    glossaryItem: { typeName: '{string}', defaultValue: '{}' }
  },
  
  namingConventions: {
    templateName: 'PascalCase',
    fieldName: 'PascalCase',
    instanceName: 'PascalCase',
    fileName: 'PascalCase'
  },
  
  options: {
    includeComments: true,
    includeMetadata: true,
    wrapOptionalFields: true
  }
};

/**
 * C++ Header Pattern
 * C++ class definitions with headers
 */
export const CPP_PRESET: ExportTemplate = {
  id: 'preset-cpp',
  name: 'C++ Header Pattern',
  platform: 'cpp',
  isPreset: true,
  description: 'C++ header pattern with base classes and user extensions.',
  
  typeFile: {
    enabled: true,
    filename: '{{TemplateName}}Base.h',
    location: 'generated',
    content: `// {{TemplateName}}Base.h
// <auto-generated>
// This file is AUTO-GENERATED and can be safely regenerated.
// DO NOT EDIT THIS FILE DIRECTLY - your changes will be lost.
// </auto-generated>

#pragma once

#include <string>
#include <vector>
#include <nlohmann/json.hpp>

namespace LocaNest {

/**
 * Base class for {{TemplateName}}
 */
class {{TemplateName}}Base {
public:
    // Data structure
    struct Data {
        std::string object_id;
        std::string name;
{{#each fields}}
        {{fieldType}} {{fieldName}}; // {{comment}}
{{/each}}
    };

protected:
    Data m_data;
    bool m_dataLoaded = false;

public:
    {{TemplateName}}Base() = default;
    virtual ~{{TemplateName}}Base() = default;

    // Apply data from JSON
    virtual void ApplyData(const nlohmann::json& jsonData) {
        OnBeforeApply(jsonData);
        
        m_data.object_id = jsonData.value("object_id", "");
        m_data.name = jsonData.value("name", "");
{{#each fields}}
        m_data.{{fieldName}} = jsonData.value("{{fieldName}}", {{fieldValue}});
{{/each}}
        
        m_dataLoaded = true;
        OnAfterApply(jsonData);
    }

    // Get data
    const Data& GetData() const { return m_data; }
    bool IsDataLoaded() const { return m_dataLoaded; }

protected:
    // Override points for custom behavior
    virtual void OnBeforeApply(const nlohmann::json& jsonData) {}
    virtual void OnAfterApply(const nlohmann::json& jsonData) {}
};

} // namespace LocaNest
`
  },
  
  instanceFile: {
    enabled: true,
    filename: '{{FileName}}.h',
    location: 'user',
    content: `// {{FileName}}.h
// USER FILE - Customize this file freely

#pragma once

#include "../generated/{{TemplateName}}Base.h"

namespace LocaNest {

/**
 * Custom implementation for {{ItemName}}
 */
class {{ItemName}} : public {{TemplateName}}Base {
public:
    {{ItemName}}() = default;
    virtual ~{{ItemName}}() = default;

protected:
    virtual void OnAfterApply(const nlohmann::json& jsonData) override {
        // Add your custom initialization here
    }

    // Add your custom methods here
{{functions}}
};

} // namespace LocaNest
`
  },
  
  fieldMappings: {
    title: { typeName: 'std::string', defaultValue: '""' },
    textarea: { typeName: 'std::string', defaultValue: '""' },
    text: { typeName: 'std::string', defaultValue: '""' },
    number: { typeName: 'int', defaultValue: '0' },
    file: { typeName: 'std::string', defaultValue: '""' },
    image: { typeName: 'std::string', defaultValue: '""' },
    url: { typeName: 'std::string', defaultValue: '""' },
    tags: { typeName: 'std::vector<std::string>', defaultValue: '{}' },
    types: { typeName: 'std::string', defaultValue: '""' },
    glossaryItem: { typeName: 'std::vector<std::string>', defaultValue: '{}' }
  },
  
  namingConventions: {
    templateName: 'PascalCase',
    fieldName: 'camelCase',
    instanceName: 'PascalCase',
    fileName: 'PascalCase'
  },
  
  options: {
    includeComments: true,
    includeMetadata: true,
    wrapOptionalFields: false
  }
};

/**
 * Roblox Database Pattern
 * Simple Lua database table pattern for exporting all objects grouped by type
 */
export const ROBLOX_DATABASE_PRESET: ExportTemplate = {
  id: 'preset-roblox-database',
  name: 'Roblox Database Pattern',
  platform: 'roblox',
  isPreset: true,
  description: 'Simple Lua table database pattern. Exports all objects grouped by template type into a single Database file.',

  // Category mapping - maps template names to database categories
  categoryMapping: {
    'Quest': 'Quests',
    'Dialog': 'Dialogs',
    'ThankWall': 'ThankWall',
    'GameStory': 'GameStory'
  },

  // Field export mappings - controls how reference fields are exported
  fieldExportMappings: {
    'glossaryItem': 'name',  // Export glossary item name by default
    'types': 'name',         // Export type name by default
    'tags': 'name',          // Export tag name by default
    'file': 'url',           // Export file URL by default
    'image': 'url',          // Export image URL by default
  },

  // Database export settings
  databaseSettings: {
    className: 'Database'
  },

  typeFile: {
    enabled: false, // Not needed for this pattern
    filename: '',
    location: '',
    content: ''
  },
  
  instanceFile: {
    enabled: true,
    filename: 'Database.lua',
    location: '',
    content: `local Database = {
{{#each categories}}
\t{{categoryName}} = {
{{#each items}}
\t\t["{{itemKey}}"] = {
{{#each fields}}
\t\t\t["{{fieldName}}"] = {{fieldValue}},
{{/each}}
\t\t},
{{/each}}
\t},

{{/each}}
}

return Database
`
  },
  
  fieldMappings: {
    title: { typeName: 'string', defaultValue: '""' },
    textarea: { typeName: 'string', defaultValue: '""' },
    text: { typeName: 'string', defaultValue: '""' },
    number: { typeName: 'number', defaultValue: '0' },
    file: { typeName: 'string', defaultValue: '""' },
    image: { typeName: 'string', defaultValue: '""' },
    url: { typeName: 'string', defaultValue: '""' },
    tags: { typeName: 'string', defaultValue: '""' },
    types: { typeName: 'string', defaultValue: '""' },
    glossaryItem: { typeName: 'string', defaultValue: '""' },
    boolean: { typeName: 'string', defaultValue: '"FALSE"' }
  },
  
  namingConventions: {
    templateName: 'PascalCase',
    fieldName: 'PascalCase', // Matches example: ["NextQuestId"]
    instanceName: 'SCREAMING_SNAKE_CASE', // For keys like Q_01
    fileName: 'PascalCase'
  },
  
  options: {
    includeComments: false,
    includeMetadata: false,
    ignoreSections: true, // Export fields directly, ignoring section structure
    wrapOptionalFields: false,
    textCase: 'original' // Text case transformation: 'uppercase', 'lowercase', or 'original'
  }
};

/**
 * Get all preset templates for code generation (excludes database patterns)
 */
export function getPresetTemplates(): ExportTemplate[] {
  return [
    ROBLOX_TABLE_PRESET,
    ROBLOX_OOP_PRESET,
    CPP_PRESET
  ];
}

/**
 * Unity C# Database Pattern
 * C# static class pattern for exporting all objects grouped by type
 */
/* export const UNITY_DATABASE_PRESET: ExportTemplate = {
  id: 'preset-unity-database',
  name: 'Unity C# Database Pattern',
  platform: 'unity',
  isPreset: true,
  description: 'C# static class database pattern. Exports all objects grouped by template type into a single Database class.',
  
  // Category mapping - maps template names to database categories
  categoryMapping: {
    'Quest': 'Quests',
    'Dialog': 'Dialogs',
    'ThankWall': 'ThankWall',
    'GameStory': 'GameStory'
  },
  
  // Field export mappings - controls how reference fields are exported
  fieldExportMappings: {
    'glossaryItem': 'name',  // Export glossary item name by default
    'types': 'name',         // Export type name by default
    'tags': 'name',          // Export tag name by default
    'file': 'url',           // Export file URL by default
    'image': 'url',          // Export image URL by default
  },
  
  typeFile: {
    enabled: false, // Not needed for this pattern
    filename: '',
    location: '',
    content: ''
  },
  
  instanceFile: {
    enabled: true,
    filename: 'Database.cs',
    location: '',
    content: `// Auto-generated Database
// This file is AUTO-GENERATED and can be safely regenerated.
// DO NOT EDIT THIS FILE DIRECTLY - your changes will be lost.

using System.Collections.Generic;

namespace LocaNest.Data
{
    public static class Database
    {
{{#each categories}}
        public static class {{categoryName}}
        {
            public static readonly Dictionary<string, Dictionary<string, object>> Data = new Dictionary<string, Dictionary<string, object>>
            {
{{#each items}}
                {
                    "{{itemKey}}", new Dictionary<string, object>
                    {
{{#each fields}}
                        { "{{fieldName}}", {{fieldValue}} },
{{/each}}
                    }
                },
{{/each}}
            };
        }

{{/each}}
    }
}
`
  },
  
  fieldMappings: {
    title: { typeName: 'string', defaultValue: '""' },
    textarea: { typeName: 'string', defaultValue: '""' },
    text: { typeName: 'string', defaultValue: '""' },
    number: { typeName: 'int', defaultValue: '0' },
    file: { typeName: 'string', defaultValue: '""' },
    image: { typeName: 'string', defaultValue: '""' },
    url: { typeName: 'string', defaultValue: '""' },
    tags: { typeName: 'string', defaultValue: '""' },
    types: { typeName: 'string', defaultValue: '""' },
    glossaryItem: { typeName: 'string', defaultValue: '""' },
    boolean: { typeName: 'bool', defaultValue: 'false' }
  },
  
  namingConventions: {
    templateName: 'PascalCase',
    fieldName: 'PascalCase', // C# convention for properties
    instanceName: 'SCREAMING_SNAKE_CASE', // For keys
    fileName: 'PascalCase'
  },
  
  options: {
    includeComments: false,
    includeMetadata: false,
    ignoreSections: true, // Export fields directly, ignoring section structure
    wrapOptionalFields: false,
    textCase: 'original'
  }
}; */

/**
 * Unity Static Strongly-Typed Access Pattern
 * C# static class pattern with folder-based hierarchy for strongly-typed access via dot notation
 */
/* export const UNITY_STATIC_TYPED_PRESET: ExportTemplate = {
  id: 'preset-unity-static-typed',
  name: 'Static Strongly-Typed Access',
  platform: 'unity',
  isPreset: true,
  description: 'C# static classes organized by folder structure. Access data using strongly-typed dot notation (e.g., Database.scenes.player_house.bed.inspect_text).',

  // Category mapping - not used for folder-based export, but required by type
  categoryMapping: {},

  // Field export mappings - controls how reference fields are exported
  fieldExportMappings: {
    'glossaryItem': 'name',  // Export glossary item name by default
    'types': 'name',         // Export type name by default
    'tags': 'name',          // Export tag name by default
    'file': 'url',           // Export file URL by default
    'image': 'url',          // Export image URL by default
  },

  // Database export settings
  databaseSettings: {
    namespace: 'Grimoire.Data',
    className: 'Database',
    translationKeysClassName: 'TranslationKeys',
    translationKeysFilename: 'TranslationKeys.cs'
  },

  typeFile: {
    enabled: false, // Not needed for this pattern
    filename: '',
    location: '',
    content: ''
  },

  instanceFile: {
    enabled: true,
    filename: 'Database.cs', // Will be replaced by custom class name
    location: '',
    content: '' // Generated programmatically by renderFolderBasedCSharpTemplate
  },
  
  fieldMappings: {
    title: { typeName: 'string', defaultValue: '""' },
    textarea: { typeName: 'string', defaultValue: '""' },
    text: { typeName: 'string', defaultValue: '""' },
    number: { typeName: 'int', defaultValue: '0' },
    file: { typeName: 'string', defaultValue: '""' },
    image: { typeName: 'string', defaultValue: '""' },
    url: { typeName: 'string', defaultValue: '""' },
    tags: { typeName: 'string[]', defaultValue: 'new string[] { }' },
    types: { typeName: 'string', defaultValue: '""' },
    glossaryItem: { typeName: 'string', defaultValue: '""' },
    boolean: { typeName: 'bool', defaultValue: 'false' }
  },
  
  namingConventions: {
    templateName: 'PascalCase',
    fieldName: 'snake_case', // For field names like inspect_text
    instanceName: 'snake_case', // For item names like player_house
    fileName: 'PascalCase'
  },
  
  options: {
    includeComments: false,
    includeMetadata: false,
    ignoreSections: true, // Export fields directly, ignoring section structure
    wrapOptionalFields: false,
    textCase: 'original'
  }
}; */

/**
 * Unity Runtime Content Database Pattern
 * C# dictionary-based pattern with template classes for runtime lookup
 */
export const UNITY_RUNTIME_DATABASE_PRESET: ExportTemplate = {
  id: 'preset-unity-runtime-database',
  name: 'Runtime Content Database',
  platform: 'unity',
  isPreset: false,
  description: 'C# dictionary with template classes for runtime lookup. Access data using Database.Library["path/to/item"] with strongly-typed template classes.',

  // Category mapping - not used for this export, but required by type
  categoryMapping: {},

  // Field export mappings - controls how reference fields are exported
  fieldExportMappings: {
    'glossaryItem': 'name',
    'types': 'name',
    'tags': 'name',
    'file': 'url',
    'image': 'url',
  },

  // Database export settings
  databaseSettings: {
    namespace: 'Grimoire.Data',
    className: 'Database',
    translationKeysClassName: 'TranslationKeys',
    translationKeysFilename: 'TranslationKeys.cs'
  },

  typeFile: {
    enabled: false,
    filename: '',
    location: '',
    content: ''
  },

  instanceFile: {
    enabled: true,
    filename: 'Database.cs',
    location: '',
    content: '' // Generated programmatically by renderRuntimeDatabaseCSharpTemplate
  },
  
  fieldMappings: {
    title: { typeName: 'string', defaultValue: '""' },
    textarea: { typeName: 'string', defaultValue: '""' },
    text: { typeName: 'string', defaultValue: '""' },
    number: { typeName: 'int', defaultValue: '0' },
    file: { typeName: 'string', defaultValue: '""' },
    image: { typeName: 'string', defaultValue: '""' },
    url: { typeName: 'string', defaultValue: '""' },
    tags: { typeName: 'string[]', defaultValue: 'new string[] { }' },
    types: { typeName: 'string', defaultValue: '""' },
    glossaryItem: { typeName: 'string', defaultValue: '""' },
    dialog: { typeName: 'string', defaultValue: '""' },
    document: { typeName: 'DocumentBlock[]', defaultValue: 'new DocumentBlock[] { }' },
    boolean: { typeName: 'bool', defaultValue: 'false' }
  },
  
  namingConventions: {
    templateName: 'snake_case', // For template class names like inspectable_object
    fieldName: 'snake_case', // For field names like inspect_text
    instanceName: 'snake_case', // For paths like scenes/player_house/bed
    fileName: 'PascalCase'
  },
  
  options: {
    includeComments: false,
    includeMetadata: false,
    ignoreSections: true,
    wrapOptionalFields: false,
    textCase: 'original'
  }
};

/**
 * Unity ID-based Records Pattern
 * C# pattern with IRecord interface, sealed record classes, and typed retrieval via Get<T>
 */
/* export const UNITY_ID_RECORDS_PRESET: ExportTemplate = {
  id: 'preset-unity-id-records',
  name: 'ID-based Records and Typed Retrieval',
  platform: 'unity',
  isPreset: true,
  description: 'C# pattern with IRecord interface, sealed record classes per blueprint, and a Db class with typed retrieval via Get<T>(id). Access data using stable IDs like Db.Get<InspectableObjectRecord>("bed_player_house").',

  // Category mapping - not used for this export, but required by type
  categoryMapping: {},

  // Field export mappings - controls how reference fields are exported
  fieldExportMappings: {
    'glossaryItem': 'name',
    'types': 'name',
    'tags': 'name',
    'file': 'url',
    'image': 'url',
  },

  // Database export settings
  databaseSettings: {
    namespace: 'Grimoire.Data',
    className: 'Db',
    translationKeysClassName: 'TranslationKeys',
    translationKeysFilename: 'TranslationKeys.cs'
  },

  typeFile: {
    enabled: false,
    filename: '',
    location: '',
    content: ''
  },

  instanceFile: {
    enabled: true,
    filename: 'Db.cs',
    location: '',
    content: '' // Generated programmatically by renderIdBasedRecordsCSharpTemplate
  },

  fieldMappings: {
    title: { typeName: 'string', defaultValue: '""' },
    textarea: { typeName: 'string', defaultValue: '""' },
    text: { typeName: 'string', defaultValue: '""' },
    number: { typeName: 'int', defaultValue: '0' },
    file: { typeName: 'string', defaultValue: '""' },
    image: { typeName: 'string', defaultValue: '""' },
    url: { typeName: 'string', defaultValue: '""' },
    tags: { typeName: 'string[]', defaultValue: 'new string[] { }' },
    types: { typeName: 'string', defaultValue: '""' },
    glossaryItem: { typeName: 'string', defaultValue: '""' },
    boolean: { typeName: 'bool', defaultValue: 'false' }
  },
  
  namingConventions: {
    templateName: 'PascalCase', // For record class names like InspectableObjectRecord
    fieldName: 'PascalCase', // For field names like InspectText
    instanceName: 'snake_case', // For IDs like bed_player_house
    fileName: 'PascalCase'
  },
  
  options: {
    includeComments: false,
    includeMetadata: false,
    ignoreSections: true,
    wrapOptionalFields: false,
    textCase: 'original'
  }
}; */

/**
 * JSON Database Pattern
 * Simple JSON structure with items grouped by category/blueprint
 */
export const JSON_DATABASE_PRESET: ExportTemplate = {
  id: 'preset-json-database',
  name: 'JSON Database',
  platform: 'json',
  isPreset: true,
  description: 'JSON database export with items grouped by category. Produces structured JSON files that can be consumed by websites, custom engines, or any application.',

  categoryMapping: {},

  fieldExportMappings: {
    'glossaryItem': 'name',
    'types': 'name',
    'tags': 'name',
    'file': 'url',
    'image': 'url',
  },

  databaseSettings: {
    className: 'database'
  },

  typeFile: {
    enabled: false,
    filename: '',
    location: '',
    content: ''
  },

  instanceFile: {
    enabled: true,
    filename: 'database.json',
    location: '',
    content: ''
  },

  fieldMappings: {
    title: { typeName: 'string', defaultValue: '""' },
    textarea: { typeName: 'string', defaultValue: '""' },
    text: { typeName: 'string', defaultValue: '""' },
    number: { typeName: 'number', defaultValue: '0' },
    file: { typeName: 'string', defaultValue: '""' },
    image: { typeName: 'string', defaultValue: '""' },
    url: { typeName: 'string', defaultValue: '""' },
    tags: { typeName: 'array', defaultValue: '[]' },
    types: { typeName: 'string', defaultValue: '""' },
    glossaryItem: { typeName: 'string', defaultValue: '""' },
    boolean: { typeName: 'boolean', defaultValue: 'false' },
    dialog: { typeName: 'string', defaultValue: '""' },
    document: { typeName: 'array', defaultValue: '[]' }
  },

  namingConventions: {
    templateName: 'snake_case',
    fieldName: 'snake_case',
    instanceName: 'snake_case',
    fileName: 'snake_case'
  },

  options: {
    includeComments: false,
    includeMetadata: false,
    ignoreSections: true,
    wrapOptionalFields: false,
    stripHtml: true,
    textCase: 'original'
  }
};

/**
 * JSON Folder-Based Pattern
 * Nested JSON objects organized by folder hierarchy
 */
export const JSON_FOLDER_BASED_PRESET: ExportTemplate = {
  id: 'preset-json-folder-based',
  name: 'JSON Folder Structure',
  platform: 'json',
  isPreset: true,
  description: 'JSON export organized by folder hierarchy. Access data via nested paths (e.g., library.scenes.player_house.bed.inspect_text). Ideal for web apps and custom engines.',

  categoryMapping: {},

  fieldExportMappings: {
    'glossaryItem': 'name',
    'types': 'name',
    'tags': 'name',
    'file': 'url',
    'image': 'url',
  },

  databaseSettings: {
    className: 'database'
  },

  typeFile: {
    enabled: false,
    filename: '',
    location: '',
    content: ''
  },

  instanceFile: {
    enabled: true,
    filename: 'database.json',
    location: '',
    content: ''
  },

  fieldMappings: {
    title: { typeName: 'string', defaultValue: '""' },
    textarea: { typeName: 'string', defaultValue: '""' },
    text: { typeName: 'string', defaultValue: '""' },
    number: { typeName: 'number', defaultValue: '0' },
    file: { typeName: 'string', defaultValue: '""' },
    image: { typeName: 'string', defaultValue: '""' },
    url: { typeName: 'string', defaultValue: '""' },
    tags: { typeName: 'array', defaultValue: '[]' },
    types: { typeName: 'string', defaultValue: '""' },
    glossaryItem: { typeName: 'string', defaultValue: '""' },
    boolean: { typeName: 'boolean', defaultValue: 'false' },
    dialog: { typeName: 'string', defaultValue: '""' },
    document: { typeName: 'array', defaultValue: '[]' }
  },

  namingConventions: {
    templateName: 'snake_case',
    fieldName: 'snake_case',
    instanceName: 'snake_case',
    fileName: 'snake_case'
  },

  options: {
    includeComments: false,
    includeMetadata: false,
    ignoreSections: true,
    wrapOptionalFields: false,
    stripHtml: true,
    textCase: 'original'
  }
};

/**
 * Roblox Static Strongly-Typed Access Pattern
 * Lua table pattern with folder-based hierarchy for strongly-typed access via dot notation
 */
export const ROBLOX_STATIC_TYPED_PRESET: ExportTemplate = {
  id: 'preset-roblox-static-typed',
  name: 'Static Strongly-Typed Access',
  platform: 'roblox',
  isPreset: true,
  description: 'Lua tables organized by folder structure. Access data using strongly-typed dot notation (e.g., Database.scenes.player_house.bed.inspect_text).',

  // Category mapping - not used for folder-based export, but required by type
  categoryMapping: {},

  // Field export mappings - controls how reference fields are exported
  fieldExportMappings: {
    'glossaryItem': 'name',  // Export glossary item name by default
    'types': 'name',         // Export type name by default
    'tags': 'name',          // Export tag name by default
    'file': 'url',           // Export file URL by default
    'image': 'url',          // Export image URL by default
  },

  // Database export settings
  databaseSettings: {
    className: 'Database'
  },

  typeFile: {
    enabled: false, // Not needed for this pattern
    filename: '',
    location: '',
    content: ''
  },

  instanceFile: {
    enabled: true,
    filename: 'Database.lua', // Will be replaced by custom class name
    location: '',
    content: '' // Generated programmatically by renderFolderBasedLuaTemplate
  },
  
  fieldMappings: {
    title: { typeName: 'string', defaultValue: '""' },
    textarea: { typeName: 'string', defaultValue: '""' },
    text: { typeName: 'string', defaultValue: '""' },
    number: { typeName: 'number', defaultValue: '0' },
    file: { typeName: 'string', defaultValue: '""' },
    image: { typeName: 'string', defaultValue: '""' },
    url: { typeName: 'string', defaultValue: '""' },
    tags: { typeName: '{string}', defaultValue: '{}' },
    types: { typeName: 'string', defaultValue: '""' },
    glossaryItem: { typeName: 'string', defaultValue: '""' },
    boolean: { typeName: 'boolean', defaultValue: 'false' }
  },
  
  namingConventions: {
    templateName: 'PascalCase',
    fieldName: 'snake_case', // For field names like inspect_text
    instanceName: 'snake_case', // For item names like player_house
    fileName: 'PascalCase'
  },
  
  options: {
    includeComments: false,
    includeMetadata: false,
    ignoreSections: true, // Export fields directly, ignoring section structure
    wrapOptionalFields: false,
    textCase: 'original'
  }
};

/**
 * Get database export presets (for Data Manager)
 */
export function getDatabasePresets(): ExportTemplate[] {
  return [
    ROBLOX_DATABASE_PRESET,
    ROBLOX_STATIC_TYPED_PRESET,
/*     UNITY_STATIC_TYPED_PRESET,
 */    UNITY_RUNTIME_DATABASE_PRESET,
    /* UNITY_ID_RECORDS_PRESET */
    JSON_DATABASE_PRESET,
    JSON_FOLDER_BASED_PRESET,
  ];
}

/**
 * Get the appropriate database preset based on game settings
 */
export function getDatabasePresetForGame(game: any): ExportTemplate {
  const engine = game?.settings?.engine?.toLowerCase();
  const codeLanguage = game?.settings?.codeLanguage?.toLowerCase();
  
  // Unity with C# uses Static Strongly-Typed Access by default
  if (engine === 'unity' || codeLanguage === 'csharp') {
    return UNITY_RUNTIME_DATABASE_PRESET;
  }
  
  // Roblox or Lua uses Roblox database preset
  if (engine === 'roblox' || codeLanguage === 'lua') {
    return ROBLOX_DATABASE_PRESET;
  }
  
  // Default to Roblox for now (most common case)
  return ROBLOX_DATABASE_PRESET;
}

/**
 * Get all presets (both code generation and database)
 */
export function getAllPresets(): ExportTemplate[] {
  return [...getPresetTemplates(), ...getDatabasePresets()];
}

/**
 * Get a preset template by ID
 */
export function getPresetById(id: string): ExportTemplate | undefined {
  return getAllPresets().find(template => template.id === id);
}

/**
 * Create a new custom template based on a preset
 */
export function createCustomFromPreset(preset: ExportTemplate, name: string): ExportTemplate {
  return {
    ...preset,
    id: generateUUID(),
    name,
    isPreset: false,
    description: `Custom template based on ${preset.name}`
  };
}

